<script setup>
import { computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const store = useStore();
const noUrlRoutes = ["/config", "/account", "/ttmm" ];

onMounted(() => {
});

function toSafeString(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function getPlanNameFromRouteState() {
  const routeStateName = toSafeString(route?.state?.plan_name);
  if (routeStateName) return routeStateName;

  if (typeof window !== "undefined") {
    return toSafeString(window.history?.state?.plan_name);
  }

  return "";
}

function resolveBreadcrumbText(targetRoute) {
  return targetRoute?.meta?.breadcrumb || targetRoute?.name;
}

function buildBreadcrumbsFromPath(path) {
  const pathSegments = path.split("/");
  pathSegments.pop();

  const breadcrumbsArray = [];
  while (pathSegments.length > 1) {
    const parentPath = pathSegments.join("/");
    const parentRoute = router.resolve({ path: parentPath });

    breadcrumbsArray.unshift({
      path: noUrlRoutes.includes(parentPath) ? null : parentRoute.path,
      text: resolveBreadcrumbText(parentRoute),
    });

    pathSegments.pop();
  }

  breadcrumbsArray.push({
    path: noUrlRoutes.includes(path) ? null : path,
    text: resolveBreadcrumbText(router.resolve({ path })),
  });

  return breadcrumbsArray;
}

const breadcrumbs = computed(() => {
  const currentPath = route.path;
  if (currentPath === "/live") {
    return [
      {
        path: null,
        text: "En vivo",
      },
      {
        path: null,
        text: "En ruta",
      },
    ];
  }

  if (currentPath === "/live/analytics") {
    return [
      {
        path: null,
        text: "En vivo",
      },
      {
        path: null,
        text: "Analíticas",
      },
    ];
  }

  if (currentPath === "/vehicles" || currentPath === "/zones") {
    return [
      {
        path: null,
        text: "Flota",
      },
      {
        path: currentPath,
        text: resolveBreadcrumbText(router.resolve({ path: currentPath })),
      },
    ];
  }

  return buildBreadcrumbsFromPath(currentPath);
});

const currentHeadingText = computed(() => {
  const routePath = toSafeString(route.path);
  if (routePath.startsWith("/plan/")) {
    const statePlanName = getPlanNameFromRouteState();
    return statePlanName || route.meta?.breadcrumb || route.name;
  }

  return breadcrumbs.value[breadcrumbs.value.length - 1]?.text;
});

const isRTL = computed(() => store.state.isRTL);
const isNavFixed = computed(() => store.state.isNavFixed);
</script>

<template>
  <nav aria-label="breadcrumb">
    <ol
      class="px-0 pt-1 pb-0 mb-0 bg-transparent breadcrumb"
      :class="!isRTL && 'me-sm-6'"
    >
      <li class="text-sm breadcrumb-item" :class="isRTL && 'ps-2'">
        <a
          :class="{
            'text-dark': isNavFixed,
            'text-white': !isNavFixed,
          }"
        >
          <i class="ni ni-box-2"></i>
        </a>
      </li>
      <li
        v-for="(crumb, index) in breadcrumbs"
        :key="index"
        class="text-sm breadcrumb-item"
        :class="{
          'text-dark': isNavFixed,
          'text-white': !isNavFixed,
          active: index === breadcrumbs.length - 1,
        }"
      >
        <router-link
          v-if="crumb.path && index !== breadcrumbs.length - 1"
          :to="crumb.path"
          class="link-active"
          :class="{
            'text-dark': isNavFixed,
            'text-white': !isNavFixed,
          }"
        >
          {{ crumb.text }}
        </router-link>
        <span
          v-else
          :class="{
            'text-dark': isNavFixed,
            'text-white': !isNavFixed,
          }"
        >
          {{ crumb.text }}
        </span>
      </li>
    </ol>
    <h5
      class="mb-0 font-weight-bolder"
      :class="{
        'text-dark': isNavFixed,
        'text-white': !isNavFixed,
      }"
    >
      {{ currentHeadingText }}
    </h5>
  </nav>
</template>

<style scoped>
</style>
